/* eslint-disable max-lines-per-function */
/* eslint-disable no-lonely-if */
/* eslint-disable no-use-before-define */
import { ref, onUnmounted, onActivated, onDeactivated, watch, computed } from 'vue';
import { XlMessage } from '@shining3d/ui-components';
import { ElMessageBox } from '@shining3d/xl-element-plus';
import { useRouter, useRoute } from 'vue-router';
import { debounce, get } from 'lodash';
import { VueCookieNext } from 'vue-cookie-next';
import commonApi from '@/api/common';
import { $getLocalStorage, $removeLocalStorage } from '@/assets/common/common';
import { useLang } from '@/hooks/lang';

const { $t } = useLang();
export function useIdleLogout( options = {}) {
	// 配置项
	const {
		idleTime = get( window, 'app.global.idleLogoutTime', 30 ) * 60, // 闲置时间（秒）
		finalCountdownTime = get( window, 'app.global.idleCountdownTime', 10 ), // 弹窗倒计时秒数
		loginRoute = '/login',
		beforeLogout = () => Promise.resolve(),
		allowedOrigins = [],
		whiteListRoutes = ['/login', '/signup'],
	} = options;

	// 基础变量
	const router = useRouter();
	const route = useRoute();
	const PAGE_UNIQUE_ID = Date.now() + Math.random().toString( 36 ).
		substr( 2, 8 );
	const isLoggedIn = ref( Boolean( getToken()));
	const remainingFinalTime = ref( finalCountdownTime ); // 改为响应式，避免数值覆盖
	let localLastActiveTime = 0; // 本地最后活动时间
	// 标记当前Tab是否为主扣减Tab（避免多Tab同时写）
	let isMainCountdownTab = false;
	// Web Worker 实例（管理所有计时：初始闲置+弹窗倒计时）
	let countdownWorker = null;

	// 全局状态KEY
	const GLOBAL_KEYS = {
		lastActive: '__USER_LAST_ACTIVE_TIME__',
		remainTime: '__GLOBAL_COUNTDOWN_REMAIN__',
		showTip: '__GLOBAL_SHOW_LOGOUT_TIP__',
		logout: '__GLOBAL_USER_HAS_LOGGED_OUT__',
		countDownLock: '__GLOBAL_COUNTDOWN_LOCK__',
	};

	// 计时器/弹窗实例（仅降级用）
	let finalCountdownTimer = null; // 降级用弹窗倒计时
	let messageBoxInstance = null;

	// 监听相关
	const iframeCleanupFns = [];
	let actionCleanupFn = null;
	let isListenersBinded = false;

	console.debug( '[闲置退出] 初始化配置', {
		idleTime,
		finalCountdownTime,
		currentRoute: route.path,
		isLoggedIn: isLoggedIn.value,
	});

	// ===================== 核心工具方法 =====================
	// 获取Token
	function getToken() {
		try {
			const token = VueCookieNext.getCookie( 'token' );
			const isValid = Boolean( token ) && token !== 'undefined' && token !== 'null' && token !== '';
			console.debug( '[闲置退出] 获取Token', { isValid });
			return isValid ? token : null;
		} catch ( e ) {
			console.error( '[闲置退出] 获取Token失败', e );
			return null;
		}
	}

	// 全局状态操作
	function getGlobalState( key ) {
		try {
			const val = localStorage.getItem( key );
			const result = val ? JSON.parse( val )?.value ?? null : null;
			return result;
		} catch ( e ) {
			localStorage.removeItem( key );
			return null;
		}
	}

	function setGlobalState( key, val ) {
		try {
			const oldVal = getGlobalState( key );
			if ( JSON.stringify( oldVal ) === JSON.stringify( val )) {
				return;
			}
			// lastActive仅当时间差>100ms才设置，避免循环
			if ( key === GLOBAL_KEYS.lastActive && val - ( oldVal || 0 ) < 100 ) {
				return;
			}
			localStorage.setItem( key, JSON.stringify({ value: val, from: PAGE_UNIQUE_ID }));
			console.debug( `[闲置退出] 设置全局状态 ${key}=${val}` );
		} catch ( e ) {
			console.error( `[闲置退出] 设置全局状态 ${key} 失败`, e );
		}
	}

	function removeGlobalState( key ) {
		try {
			localStorage.removeItem( key );
		} catch ( e ) {
			console.error( `[闲置退出] 删除全局状态 ${key} 失败`, e );
		}
	}

	// 强制清理所有全局状态
	function clearAllGlobalState() {
		console.debug( '[闲置退出] 清理所有全局状态' );
		Object.values( GLOBAL_KEYS ).forEach(( key ) => removeGlobalState( key ));
		// 重置主Tab标记
		isMainCountdownTab = false;
	}

	// 释放全局倒计时锁
	function releaseCountDownLock() {
		removeGlobalState( GLOBAL_KEYS.countDownLock );
		isMainCountdownTab = false;
	}

	// 尝试获取全局扣减锁（单例扣减）
	function tryAcquireCountDownLock() {
		const lockHolder = getGlobalState( GLOBAL_KEYS.countDownLock );
		// 锁不存在 → 当前Tab获取；锁存在且是自己 → 保持；否则拒绝
		const canAcquire = !lockHolder || lockHolder === PAGE_UNIQUE_ID;
		if ( canAcquire ) {
			setGlobalState( GLOBAL_KEYS.countDownLock, PAGE_UNIQUE_ID );
			isMainCountdownTab = true; // 标记当前Tab为主扣减Tab
			console.debug( '[闲置退出] 当前Tab获取扣减锁，标记为主扣减Tab' );
		} else {
			isMainCountdownTab = false; // 非主Tab，仅读取不写入
		}
		return canAcquire;
	}

	// ✨ 重构Worker代码：同时处理初始闲置计时+弹窗倒计时
	function getWorkerCode() {
		return `
			// Worker内部状态
			let idleTimer = null; // 初始闲置计时
			let countdownTimer = null; // 弹窗倒计时
			let currentIdleTime = 60; // 默认闲置时间（秒）
			let currentFinalTime = 60; // 默认弹窗倒计时（秒）

			// 监听主线程消息
			self.onmessage = (e) => {
				const { type, payload } = e.data;
				console.debug('[Worker] 收到主线程消息', type);

				switch(type) {
					// 重置初始闲置计时
					case 'reset-idle':
						// 清除所有现有定时器
						clearTimeout(idleTimer);
						clearInterval(countdownTimer);
						// 更新配置
						currentIdleTime = payload.idleTime;
						currentFinalTime = payload.finalTime;
						// 启动初始闲置计时（毫秒）
						idleTimer = setTimeout(() => {
							// 闲置时间到，通知主线程弹弹窗
							self.postMessage({ type: 'idle-timeout' });
						}, currentIdleTime * 1000);
						break;

					// 启动弹窗倒计时
					case 'start-countdown':
						clearInterval(countdownTimer);
						// 每秒扣减，通知主线程
						countdownTimer = setInterval(() => {
							self.postMessage({ type: 'countdown-tick' });
						}, 1000);
						break;

					// 停止所有计时
					case 'stop-all':
						clearTimeout(idleTimer);
						clearInterval(countdownTimer);
						break;

					default:
						break;
				}
			};

			// Worker终止时清理定时器
			self.onclose = () => {
				clearTimeout(idleTimer);
				clearInterval(countdownTimer);
			};
		`;
	}

	// ✨ 创建Worker：管理所有计时逻辑
	function createCountdownWorker() {
		// 避免重复创建
		if ( countdownWorker ) {
			return;
		}
		try {
			// 1. 创建Blob URL（内联Worker，无跨域）
			const blob = new Blob([getWorkerCode()], { type: 'application/javascript' });
			const workerUrl = URL.createObjectURL( blob );
			// 2. 实例化Worker
			countdownWorker = new Worker( workerUrl );
			// 3. 释放Blob URL
			URL.revokeObjectURL( workerUrl );

			// ✨ 监听Worker的消息（核心：接收Worker的计时指令）
			countdownWorker.onmessage = ( e ) => {
				const { type } = e.data;
				switch ( type ) {
				// Worker通知：初始闲置时间到，该弹弹窗了
				case 'idle-timeout':
					handleWorkerIdleTimeout();
					break;
					// Worker通知：弹窗倒计时扣减1秒
				case 'countdown-tick':
					handleWorkerCountdownTick();
					break;
				default:
					break;
				}
			};

			// Worker错误处理
			countdownWorker.onerror = ( err ) => {
				console.error( '[闲置退出] Worker错误：', err );
				destroyCountdownWorker();
				fallbackToLocalTimer(); // 降级到主线程定时器
			};
			console.debug( '[闲置退出] 内联Worker创建成功（管理所有计时）' );
		} catch ( e ) {
			console.error( '[闲置退出] 创建Worker失败，降级为本地定时器：', e );
			fallbackToLocalTimer();
		}
	}

	// 销毁Worker
	function destroyCountdownWorker() {
		if ( countdownWorker ) {
			countdownWorker.postMessage({ type: 'stop-all' }); // 通知Worker停止所有计时
			countdownWorker.terminate(); // 强制终止
			countdownWorker = null;
			console.debug( '[闲置退出] Worker已销毁' );
		}
	}

	// ✨ Worker通知：初始闲置时间到（处理弹窗逻辑）
	function handleWorkerIdleTimeout() {
		console.debug( '[闲置退出] Worker通知：闲置时间到，触发弹窗' );
		if ( !needStartListen.value ) {
			return;
		}
		// 强制设置全局弹窗状态（仅第一次触发时设置）
		if ( !getGlobalState( GLOBAL_KEYS.showTip )) {
			setGlobalState( GLOBAL_KEYS.showTip, true );
			setGlobalState( GLOBAL_KEYS.remainTime, finalCountdownTime );
			remainingFinalTime.value = finalCountdownTime; // 同步本地响应式状态
		}
		startFinalCountdown();
	}

	// ✨ 修复核心：Worker通知扣减时，仅主Tab更新全局状态，其他Tab只读
	function handleWorkerCountdownTick() {
		const currentShowTip = getGlobalState( GLOBAL_KEYS.showTip );
		if ( !currentShowTip ) {
			resetLocalState();
			return;
		}

		// 1. 先获取最新的全局倒计时数值（保证多Tab同步）
		let latestRemain = getGlobalState( GLOBAL_KEYS.remainTime ) || finalCountdownTime;

		// 2. 只有主扣减Tab才能执行扣减和写入全局状态
		if ( isMainCountdownTab ) {
			if ( latestRemain > 0 ) {
				latestRemain -= 1;
				setGlobalState( GLOBAL_KEYS.remainTime, latestRemain ); // 主Tab才写入
				console.debug( `[闲置退出] 主Tab扣减剩余时间：${latestRemain}秒` );
			}

			// 3. 倒计时结束，执行退出（仅主Tab处理）
			if ( latestRemain <= 0 ) {
				console.debug( '[闲置退出] 倒计时结束，执行自动退出' );
				resetLocalState();
				logout();
				return;
			}
		} else {
			// 非主Tab：仅读取全局状态，不写入
			console.debug( `[闲置退出] 非主Tab同步倒计时：${latestRemain}秒` );
		}

		// 4. 同步本地响应式状态+更新DOM（所有Tab都做）
		remainingFinalTime.value = latestRemain;
		if ( !document.hidden ) {
			const dom = document.querySelector( '.el-message-box__message .countdown-text' );
			if ( dom && dom.innerText !== latestRemain ) {
				// 避免重复赋值
				dom.innerText = latestRemain;
			}
		}
	}

	// 降级方案：主线程定时器（仅Worker失败时用，仍会受休眠影响）
	function fallbackToLocalTimer() {
		// 降级：初始闲置计时（主线程setTimeout，休眠会停滞）
		const now = Date.now();
		localLastActiveTime = now;
		const idleTimer = setTimeout(() => {
			handleWorkerIdleTimeout();
		}, idleTime * 1000 );

		// 降级：弹窗倒计时（仅主Tab执行扣减）
		if ( finalCountdownTimer ) {
			return;
		}
		finalCountdownTimer = setInterval(() => {
			if ( isMainCountdownTab ) {
				// 降级时也保证单Tab扣减
				handleWorkerCountdownTick();
			}
		}, 1000 );

		// 记录降级定时器（用于清理）
		iframeCleanupFns.push(() => {
			clearTimeout( idleTimer );
			clearInterval( finalCountdownTimer );
		});
		console.debug( '[闲置退出] 已降级到主线程定时器（注意：休眠会影响计时）' );
	}

	// 重置本地状态
	function resetLocalState() {
		console.debug( '[闲置退出] 重置本地状态' );
		// 销毁Worker
		destroyCountdownWorker();
		// 清理降级定时器
		if ( finalCountdownTimer ) {
			clearInterval( finalCountdownTimer );
			finalCountdownTimer = null;
		}
		// 关闭弹窗
		if ( messageBoxInstance ) {
			ElMessageBox.close();
			messageBoxInstance = null;
		}
		// 重置倒计时数值+释放锁
		remainingFinalTime.value = finalCountdownTime;
		releaseCountDownLock();
	}

	// ===================== 核心逻辑：重置闲置计时（交给Worker） =====================
	const needStartListen = computed(() => {
		const inWhiteList = whiteListRoutes.includes( route.path );
		const result = isLoggedIn.value && !inWhiteList;
		console.debug( '[闲置退出] 监听状态判断', { isLoggedIn: isLoggedIn.value, inWhiteList, result });
		return result;
	});

	function resetIdleTimer() {
		console.debug( '[闲置退出] 触发重置计时（用户操作/Tab切回）' );
		if ( !needStartListen.value ) {
			resetLocalState();
			clearAllGlobalState();
			clearAllListeners();
			return;
		}

		// 1. 重置本地+全局状态
		resetLocalState();
		clearAllGlobalState();
		// 2. 初始化监听
		initAllListeners();
		// 3. ✨ 通知Worker重置初始闲置计时（核心：替代主线程setTimeout）
		const now = Date.now();
		localLastActiveTime = now;
		setGlobalState( GLOBAL_KEYS.lastActive, now );

		// 创建Worker（确保存在）
		createCountdownWorker();
		// 向Worker发送重置指令，传递配置参数
		if ( countdownWorker ) {
			countdownWorker.postMessage({
				type: 'reset-idle',
				payload: {
					idleTime: idleTime, // 初始闲置时间（秒）
					finalTime: finalCountdownTime, // 弹窗倒计时（秒）
				},
			});
		}
		console.debug( `[闲置退出] 已通知Worker重置闲置计时，${idleTime}秒后触发` );
	}

	// 启动弹窗倒计时（交给Worker）
	function startFinalCountdown() {
		console.debug( '[闲置退出] 启动弹窗倒计时逻辑' );
		if ( !needStartListen.value ) {
			return;
		}

		// 获取全局状态
		const globalShowTip = getGlobalState( GLOBAL_KEYS.showTip );
		let globalRemain = getGlobalState( GLOBAL_KEYS.remainTime ) || finalCountdownTime;
		if ( globalRemain <= 0 ) {
			globalRemain = finalCountdownTime;
			setGlobalState( GLOBAL_KEYS.remainTime, globalRemain ); // 兜底重置
		}

		if ( !globalShowTip ) {
			resetLocalState();
			return;
		}

		// 显示弹窗（仅当前Tab可见+未创建弹窗时）
		if ( !document.hidden && !messageBoxInstance ) {
			console.debug( '[闲置退出] 当前Tab可见，创建弹窗' );
			const idleMinutes = idleTime / 60;
			// 弹窗内容中加入countdown-text标记，用于更新数值
			const message = $t( 'idleLogout.tip.desc', {
				idleMinutes,
				remainingFinalTime: `<span class="countdown-text">${globalRemain}</span>`,
			});
			try {
				messageBoxInstance = ElMessageBox.confirm( message, $t( 'application.detail.tips' ), {
					confirmButtonText: $t( 'idleLogout.tip.maintain' ),
					showCancelButton: false,
					closeOnClickModal: false,
					closeOnPressEscape: false,
					dangerouslyUseHTMLString: true,
					beforeClose: () => {
						console.debug( '[闲置退出] 点击保持登录，重置计时' );
						resetIdleTimer();
					},
				});
			} catch ( e ) {
				console.error( '[闲置退出] 弹窗创建失败', e );
			}
		}

		// 仅主Tab执行扣减（获取锁）
		tryAcquireCountDownLock();

		// ✨ 通知Worker启动弹窗倒计时（仅第一次启动时发送）
		if ( countdownWorker && !finalCountdownTimer ) {
			countdownWorker.postMessage({ type: 'start-countdown' });
		}
	}

	// ===================== 监听事件（原有逻辑不变） =====================
	// 判断是否为其他Tab触发的storage事件
	function isFromOtherPage( e ) {
		if ( !e.newValue ) {
			return false;
		}
		try {
			const storeData = JSON.parse( e.newValue );
			return storeData.from !== PAGE_UNIQUE_ID;
		} catch ( err ) {
			console.log( err );
			return false;
		}
	}

	// 监听用户操作
	function listenUserAction() {
		const handleAction = debounce(() => resetIdleTimer(), 500, { leading: true, trailing: false });
		const handle = () => handleAction();
		const handleKeydown = ( e ) => {
			const ignoreKeys = ['Shift', 'Ctrl', 'Alt', 'Meta', 'CapsLock', 'Tab'];
			!ignoreKeys.includes( e.key ) && handleAction();
		};

		document.addEventListener( 'click', handle );
		document.addEventListener( 'keydown', handleKeydown );
		document.addEventListener( 'touchstart', handle );

		actionCleanupFn = () => {
			document.removeEventListener( 'click', handle );
			document.removeEventListener( 'keydown', handleKeydown );
			document.removeEventListener( 'touchstart', handle );
			handleAction.cancel();
		};
	}

	// 监听iframe操作
	function listenIframeAction() {
		const handleIframeAction = debounce(() => resetIdleTimer(), 500 );
		// 监听postMessage
		const handlePostMsg = ( e ) => {
			if ( allowedOrigins.length && !allowedOrigins.includes( e.origin )) {
				return;
			}
			e.data.type === 'iframe-user-action' && handleIframeAction();
		};
		window.addEventListener( 'message', handlePostMsg );
		iframeCleanupFns.push(() => window.removeEventListener( 'message', handlePostMsg ));

		// 绑定iframe内部事件
		document.querySelectorAll( 'iframe' ).forEach(( iframe ) => {
			const handleLoad = () => {
				try {
					const doc = iframe.contentDocument || iframe.contentWindow.document;
					const click = () => handleIframeAction();
					const keydown = ( e ) => {
						const ignore = ['Shift', 'Ctrl', 'Alt', 'Meta', 'CapsLock', 'Tab'];
						!ignore.includes( e.key ) && handleIframeAction();
					};
					doc.addEventListener( 'click', click );
					doc.addEventListener( 'keydown', keydown );
					iframeCleanupFns.push(() => {
						doc.removeEventListener( 'click', click );
						doc.removeEventListener( 'keydown', keydown );
					});
				} catch ( e ) {
					console.log( e );
				}
			};
			iframe.addEventListener( 'load', handleLoad );
			iframeCleanupFns.push(() => iframe.removeEventListener( 'load', handleLoad ));
		});
	}

	// Tab可见性变化（修复：切回时同步最新倒计时）
	function handleVisibilityChange() {
		console.debug( '[闲置退出] Tab可见性变化', { hidden: document.hidden });
		if ( !needStartListen.value ) {
			return;
		}

		// 切回可见时重置计时+同步最新倒计时
		if ( !document.hidden ) {
			console.debug( '[闲置退出] Tab切回可见，触发重置计时+同步倒计时' );
			resetIdleTimer();
			// 同步最新的全局倒计时数值到DOM
			const latestRemain = getGlobalState( GLOBAL_KEYS.remainTime ) || finalCountdownTime;
			const dom = document.querySelector( '.el-message-box__message .countdown-text' );
			if ( dom ) {
				dom.innerText = latestRemain;
			}
		} else {
			// 隐藏时关闭弹窗DOM
			if ( messageBoxInstance ) {
				console.debug( '[闲置退出] Tab隐藏，关闭弹窗DOM' );
				ElMessageBox.close();
				messageBoxInstance = null;
			}
		}
	}

	// 监听多Tab同步（修复：非主Tab仅同步显示，不扣减）
	function handleStorageChange( e ) {
		if ( !needStartListen.value || !isFromOtherPage( e )) {
			return;
		}

		// 其他Tab退出，同步登出
		if ( e.key === GLOBAL_KEYS.logout ) {
			console.debug( '[闲置退出] 其他Tab已退出，同步登出' );
			resetLocalState();
			isLoggedIn.value = false;
			XlMessage({ message: $t( 'idleLogout.tip.autoLogout' ), type: 'warning' });
			router.replace({ path: loginRoute });
			return;
		}

		// 其他Tab更新活动时间，同步重置
		if ( e.key === GLOBAL_KEYS.lastActive ) {
			const newGlobalTime = getGlobalState( GLOBAL_KEYS.lastActive ) || 0;
			if ( newGlobalTime - localLastActiveTime > 100 ) {
				console.debug( '[闲置退出] 其他Tab更新活动时间，重置计时' );
				localLastActiveTime = newGlobalTime;
				resetIdleTimer();
			}
		}

		// 同步剩余秒数（仅更新显示，不扣减）
		if ( e.key === GLOBAL_KEYS.remainTime && e.newValue ) {
			const latestRemain = getGlobalState( GLOBAL_KEYS.remainTime ) || finalCountdownTime;
			remainingFinalTime.value = latestRemain;
			if ( !document.hidden ) {
				const dom = document.querySelector( '.el-message-box__message .countdown-text' );
				if ( dom ) {
					dom.innerText = latestRemain;
				}
			}
		}
	}

	// 初始化所有监听
	function initAllListeners() {
		if ( isListenersBinded || !needStartListen.value ) {
			return;
		}
		listenUserAction();
		listenIframeAction();
		document.addEventListener( 'visibilitychange', handleVisibilityChange );
		window.addEventListener( 'storage', handleStorageChange );
		isListenersBinded = true;
	}

	// 清除所有监听
	function clearAllListeners() {
		if ( !isListenersBinded ) {
			return;
		}
		actionCleanupFn && actionCleanupFn();
		iframeCleanupFns.forEach(( fn ) => fn());
		document.removeEventListener( 'visibilitychange', handleVisibilityChange );
		window.removeEventListener( 'storage', handleStorageChange );
		isListenersBinded = false;
	}

	// ===================== 退出登录逻辑 =====================
	async function logout() {
		try {
			if ( !needStartListen.value ) {
				return;
			}
			// 前置操作
			await beforeLogout();
			const userId = $getLocalStorage( 'userInfo' )?.userID;
			userId && await commonApi.userLogout({ logoutType: 'openCloudSite', userId });

			// 清除凭证
			VueCookieNext.removeCookie( 'token' );
			VueCookieNext.removeCookie( 'user_id' );
			VueCookieNext.removeCookie( 'uTitle' );
			$removeLocalStorage( 'userInfo' );
			$removeLocalStorage( 'currentOrganization' );

			// 全局广播退出
			setGlobalState( GLOBAL_KEYS.logout, Date.now());
			clearAllGlobalState();

			// 跳转+提示
			router.replace({ path: loginRoute });
			XlMessage({ message: $t( 'idleLogout.tip.autoLogout' ), type: 'warning' });
		} catch ( e ) {
			console.error( '[闲置退出] 自动退出失败', e );
			XlMessage({ message: $t( 'idleLogout.tip.autoLogoutFail' ), type: 'error' });
		} finally {
			resetLocalState();
			clearAllListeners();
		}
	}

	// ===================== 生命周期 =====================
	function updateIdleStatus() {
		isLoggedIn.value = Boolean( getToken());
		setTimeout(() => resetIdleTimer(), 300 );
	}

	function initIdle() {
		console.debug( '[闲置退出] 初始化完成' );
		// 首次初始化
		updateIdleStatus();

		// 监听路由变化
		watch(
			() => route.fullPath,
			() => resetIdleTimer(),
			{ flush: 'post' },
		);

		// 监听登录态变化
		watch(
			() => isLoggedIn.value,
			() => resetIdleTimer(),
		);
	}

	// 组件卸载
	onUnmounted(() => {
		resetLocalState();
		clearAllGlobalState();
		clearAllListeners();
	});

	// 组件失活/激活
	onDeactivated(() => {
		releaseCountDownLock();
	});

	onActivated(() => {
		if ( needStartListen.value ) {
			resetIdleTimer();
			// 激活时同步最新倒计时
			const latestRemain = getGlobalState( GLOBAL_KEYS.remainTime ) || finalCountdownTime;
			remainingFinalTime.value = latestRemain;
		}
	});

	// 暴露方法
	return { initIdle, logout, updateIdleStatus, isLoggedIn, remainingFinalTime };
}
